import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Eye,
  Download,
  Trash2,
  Search,
  AlertCircle,
  RefreshCcw,
  RotateCw,
  X,
  CheckCircle2,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Image,
} from "lucide-react";
import { cn } from "../lib/utils";
import ImageUploadModal from '../components/ImageUploadModal';

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in">
      <div
        className={cn(
          "flex items-center gap-2 px-4 py-3 rounded-lg shadow-sm",
          type === "success" &&
            "bg-nn-teal-50 text-nn-teal-800 border border-nn-teal-200",
          type === "error" && "bg-red-50 text-red-800 border border-red-200"
        )}
      >
        {type === "success" && (
          <CheckCircle2 className="h-5 w-5 text-nn-teal-500" />
        )}
        {type === "error" && <AlertCircle className="h-5 w-5 text-red-500" />}
        <p className="text-sm font-medium">{message}</p>
        <button
          onClick={onClose}
          className="ml-2 text-nn-gray-500 hover:text-nn-gray-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const AdminInterface = () => {
  const [analyses, setAnalyses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [reanalyzeLoading, setReanalyzeLoading] = useState(null);
  const [toast, setToast] = useState(null);
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);

  // Show toast message
  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  useEffect(() => {
    fetchAnalyses();
    fetchUsers();
  }, []);

  const fetchAnalyses = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/analyses`);
      if (!response.ok) throw new Error("Failed to fetch analyses");
      const data = await response.json();
      setAnalyses(data);
      setError(null);
    } catch (error) {
      setError("Failed to fetch analyses");
      console.error("Error fetching analyses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReanalyze = async (analysis) => {
    if (
      !window.confirm(
        "Weet u zeker dat u deze analyse opnieuw wilt uitvoeren? Dit zal de bestaande analyse bijwerken."
      )
    ) {
      return;
    }

    try {
      setReanalyzeLoading(analysis.id);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/analyses/${analysis.id}/reanalyze`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ text: analysis.text }),
        }
      );

      if (!response.ok) throw new Error("Failed to reanalyze");

      const updatedAnalysis = await response.json();

      setAnalyses((prevAnalyses) =>
        prevAnalyses.map((a) => (a.id === analysis.id ? updatedAnalysis : a))
      );

      showToast(`Analyse #${analysis.id} succesvol opnieuw uitgevoerd`);
      setError(null);
    } catch (error) {
      setError("Failed to reanalyze case");
      showToast(`Opnieuw analyseren van zaak #${analysis.id} mislukt`, "error");
      console.error("Error reanalyzing case:", error);
    } finally {
      setReanalyzeLoading(null);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Weet u zeker dat u deze analyse wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt."
      )
    ) {
      return;
    }

    try {
      setDeleteLoading(id);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/analyses/${id}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) throw new Error("Failed to delete analysis");

      setAnalyses((prevAnalyses) =>
        prevAnalyses.filter((analysis) => analysis.id !== id)
      );
      setError(null);
    } catch (error) {
      setError("Failed to delete analysis");
      console.error("Error deleting analysis:", error);
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleDownload = (analysis) => {
    const dataStr = JSON.stringify(analysis, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", `analysis-${analysis.id}.json`);
    linkElement.click();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("nl-NL", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const truncateText = (text, length = 50) => {
    if (!text) return "-";
    return text.length > length ? `${text.substring(0, length)}...` : text;
  };

  const filteredAnalyses = analyses.filter((analysis) => {
    if (!searchTerm) return true;

    const searchTermLower = searchTerm.toLowerCase();

    // Search in text
    const textMatch = analysis.text?.toLowerCase().includes(searchTermLower);

    // Search in zaaknummer
    const zaaknummerMatch = analysis.json_output?.basis_informatie?.zaaknummer
      ?.toLowerCase()
      .includes(searchTermLower);

    // Search in document type
    const documentTypeMatch = analysis.document_type
      ?.toLowerCase()
      .includes(searchTermLower);

    // Search in company names
    const bedrijvenMatch =
      Array.isArray(
        analysis.json_output?.basis_informatie?.betrokken_bedrijven
      ) &&
      analysis.json_output.basis_informatie.betrokken_bedrijven.some(
        (bedrijf) => {
          if (typeof bedrijf === "object") {
            return (
              bedrijf.klager?.toLowerCase().includes(searchTermLower) ||
              bedrijf.verweerder?.toLowerCase().includes(searchTermLower)
            );
          }
          return bedrijf?.toString().toLowerCase().includes(searchTermLower);
        }
      );

    // Search in hoofdcategorie
    const hoofdcategorieMatch = analysis.json_output?.metadata?.hoofdcategorie
      ?.toLowerCase()
      .includes(searchTermLower);

    return (
      textMatch ||
      zaaknummerMatch ||
      documentTypeMatch ||
      bedrijvenMatch ||
      hoofdcategorieMatch
    );
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  const sortedAnalyses = [...filteredAnalyses].sort((a, b) => {
    if (sortField === "id") {
      return sortDirection === "asc" ? a.id - b.id : b.id - a.id;
    }
    if (sortField === "datum") {
      const aDate = a.json_output?.basis_informatie?.datum_uitspraak;
      const bDate = b.json_output?.basis_informatie?.datum_uitspraak;
      return sortDirection === "asc"
        ? new Date(aDate) - new Date(bDate)
        : new Date(bDate) - new Date(aDate);
    }
    if (sortField === "zaaknummer") {
      const aVal = a.json_output?.basis_informatie?.zaaknummer || "";
      const bVal = b.json_output?.basis_informatie?.zaaknummer || "";
      return sortDirection === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    if (sortField === "type") {
      const aVal = a.document_type || "";
      const bVal = b.document_type || "";
      return sortDirection === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    if (sortField === "bedrijven") {
      const aVal = Array.isArray(
        a.json_output?.basis_informatie?.betrokken_bedrijven
      )
        ? (
            a.json_output.basis_informatie.betrokken_bedrijven[0] || ""
          ).toString()
        : "";
      const bVal = Array.isArray(
        b.json_output?.basis_informatie?.betrokken_bedrijven
      )
        ? (
            b.json_output.basis_informatie.betrokken_bedrijven[0] || ""
          ).toString()
        : "";

      // Handle cases where values might be undefined/null
      if (!aVal && !bVal) return 0;
      if (!aVal) return sortDirection === "asc" ? 1 : -1;
      if (!bVal) return sortDirection === "asc" ? -1 : 1;

      return sortDirection === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    return 0;
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users`);
      if (!response.ok) throw new Error("Failed to fetch users");
      const data = await response.json();
      setUsers(data);
      setError(null);
    } catch (error) {
      setError("Failed to fetch users");
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/${userId}/role`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ role: newRole }),
        }
      );
      if (!response.ok) throw new Error("Failed to update role");
      const updatedUser = await response.json();
      setUsers(users.map((user) => (user.id === userId ? updatedUser : user)));
      showToast("Role updated successfully");
    } catch (error) {
      setError("Failed to update role");
      console.error("Error updating role:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-nn-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nn-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div>
        <div className="container mx-auto px-6 py-16">
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <h1 className="text-2xl font-bold text-nn-500">
                Beheer analyses
              </h1>
              <div className="flex items-center gap-4">
                <Link
                  to="/scan"
                  className="flex items-center gap-2 px-4 py-2 bg-nn-500 text-white rounded-md hover:bg-nn-600 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Voeg analyse toe
                </Link>
              </div>
            </div>

            {/* Search and Actions Bar */}
            <div className="bg-white rounded-lg shadow-sm mb-6">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="flex-1 w-full">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-nn-gray-400 h-5 w-5" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Zoek analyses..."
                      className="w-full pl-10 pr-4 py-2 border border-nn-gray-200 rounded-lg focus:ring-2 focus:ring-nn-500 focus:border-nn-500"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={fetchAnalyses}
                    className="flex items-center gap-2 px-4 py-2 text-nn-gray-600 hover:text-nn-500 border border-nn-gray-200 rounded-lg hover:border-nn-gray-300 transition-colors"
                  >
                    <RefreshCcw className="h-4 w-4" />
                    Vernieuwen
                  </button>
                </div>
              </div>
            </div>

            {/* Error Message - keeping red for clear error feedback */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="py-10">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Beheer uitspraken Section */}
                <div className="mb-12">
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    {/* Analyses Table */}
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-nn-gray-200">
                          <thead className="bg-nn-gray-50">
                            <tr>
                              {[
                                { id: "id", label: "ID" },
                                { id: "datum", label: "Datum" },
                                { id: "zaaknummer", label: "Zaaknummer" },
                                { id: "type", label: "Type" },
                                { id: "bedrijven", label: "Bedrijven" },
                                {
                                  id: "hoofdcategorie",
                                  label: "Hoofdcategorie",
                                },
                                {
                                  id: "actions",
                                  label: "Acties",
                                  sortable: false,
                                },
                              ].map((header) => (
                                <th
                                  key={header.id}
                                  className={`px-6 py-3 text-left text-xs font-medium text-nn-gray-500 uppercase tracking-wider ${
                                    header.sortable !== false
                                      ? "cursor-pointer hover:bg-nn-gray-100"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    header.sortable !== false &&
                                    handleSort(header.id)
                                  }
                                >
                                  <div className="flex items-center gap-2">
                                    {header.label}
                                    {header.sortable !== false && (
                                      <span className="text-nn-gray-400">
                                        {getSortIcon(header.id)}
                                      </span>
                                    )}
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-nn-gray-200">
                            {sortedAnalyses.map((analysis) => (
                              <tr
                                key={analysis.id}
                                className="hover:bg-nn-gray-50"
                              >
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-nn-gray-900">
                                  {analysis.id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-nn-gray-500">
                                  {formatDate(
                                    analysis.json_output?.basis_informatie
                                      ?.datum_uitspraak
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-nn-gray-900">
                                  {analysis.json_output?.basis_informatie
                                    ?.zaaknummer || "-"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {analysis.document_type ? (
                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-nn-50 text-nn-700">
                                      {analysis.document_type
                                        .charAt(0)
                                        .toUpperCase() +
                                        analysis.document_type.slice(1)}
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex flex-wrap gap-1">
                                    {Array.isArray(
                                      analysis.json_output?.basis_informatie
                                        ?.betrokken_bedrijven
                                    ) &&
                                    analysis.json_output.basis_informatie
                                      .betrokken_bedrijven.length > 0 ? (
                                      <>
                                        {analysis.json_output.basis_informatie.betrokken_bedrijven
                                          .slice(0, 2)
                                          .map((bedrijf, idx) => (
                                            <span
                                              key={idx}
                                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-nn-gray-100 text-nn-gray-700"
                                            >
                                              {typeof bedrijf === "object"
                                                ? bedrijf.klager ||
                                                  bedrijf.verweerder
                                                : bedrijf}
                                            </span>
                                          ))}
                                        {analysis.json_output.basis_informatie
                                          .betrokken_bedrijven.length > 2 && (
                                          <span className="text-xs text-nn-gray-500">
                                            +
                                            {analysis.json_output
                                              .basis_informatie
                                              .betrokken_bedrijven.length - 2}
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <span>-</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex flex-wrap gap-1">
                                    {analysis.json_output?.metadata
                                      ?.hoofdcategorie ? (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-nn-gray-100 text-nn-gray-700">
                                        {
                                          analysis.json_output.metadata
                                            .hoofdcategorie
                                        }
                                      </span>
                                    ) : (
                                      <span>-</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex justify-end gap-2">
                                    <Link
                                      to={`/analyses/${analysis.id}`}
                                      className="text-nn-500 hover:text-nn-600"
                                      title="Bekijk analyse"
                                    >
                                      <Eye className="h-5 w-5" />
                                    </Link>
                                    <button
                                      onClick={() => handleReanalyze(analysis)}
                                      disabled={
                                        reanalyzeLoading === analysis.id
                                      }
                                      className={`text-nn-500 hover:text-nn-600 ${
                                        reanalyzeLoading === analysis.id
                                          ? "opacity-50 cursor-not-allowed"
                                          : ""
                                      }`}
                                      title="Opnieuw analyseren"
                                    >
                                      <RotateCw
                                        className={`h-5 w-5 ${
                                          reanalyzeLoading === analysis.id
                                            ? "animate-spin"
                                            : ""
                                        }`}
                                      />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(analysis.id)}
                                      disabled={deleteLoading === analysis.id}
                                      className={`text-red-500 hover:text-red-600 ${
                                        deleteLoading === analysis.id
                                          ? "opacity-50 cursor-not-allowed"
                                          : ""
                                      }`}
                                      title="Verwijder analyse"
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setSelectedAnalysisId(analysis.id);
                                        setShowImageUpload(true);
                                      }}
                                      className="text-nn-500 hover:text-nn-600"
                                      title="Upload image"
                                    >
                                      <Image className="h-5 w-5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Empty State */}
                      {filteredAnalyses.length === 0 && (
                        <div className="text-center py-12">
                          <p className="text-nn-gray-500 text-sm">
                            Geen analyses gevonden
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Beheer gebruikers Section */}
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-nn-gray-900 mb-6">
                    Beheer gebruikers
                  </h2>
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-nn-gray-200">
                        <thead className="bg-nn-gray-50">
                          <tr>
                            {[
                              "ID",
                              "First Name",
                              "Last Name",
                              "Role",
                              "Actions",
                            ].map((header) => (
                              <th
                                key={header}
                                className="px-6 py-3 text-left text-xs font-medium text-nn-gray-500 uppercase tracking-wider"
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-nn-gray-200">
                          {users.map((user) => (
                            <tr key={user.id} className="hover:bg-nn-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-nn-gray-900">
                                {user.id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-nn-gray-500">
                                {user.firstName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-nn-gray-500">
                                {user.lastName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <select
                                  value={user.role}
                                  onChange={(e) =>
                                    handleRoleChange(user.id, e.target.value)
                                  }
                                  className="px-2 py-1 text-xs font-medium rounded-full bg-nn-50 text-nn-700"
                                >
                                  <option value="user">User</option>
                                  <option value="admin">Admin</option>
                                </select>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() =>
                                    handleRoleChange(
                                      user.id,
                                      user.role === "admin" ? "user" : "admin"
                                    )
                                  }
                                  className="text-nn-teal-600 hover:text-nn-teal-700"
                                >
                                  {user.role === "admin"
                                    ? "Demote to User"
                                    : "Promote to Admin"}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showImageUpload && selectedAnalysisId && (
        <ImageUploadModal
          zaakId={selectedAnalysisId}
          onClose={() => {
            setShowImageUpload(false);
            setSelectedAnalysisId(null);
          }}
          onSuccess={() => {
            fetchAnalyses();
            showToast('Image uploaded successfully');
          }}
        />
      )}
    </div>
  );
};

export default AdminInterface;
